/**
 * LEGACY / DEPRECATED
 *
 * This file is a compatibility wrapper for legacy Options API components that still rely on
 * the global mixin contract.
 *
 * New code should not use this mixin. Use `useGlobal` from `@/hooks/useGlobal.ts` instead.
 */

import { defineComponent } from 'vue';
import {
    useGlobal,
    type GlobalMethods,
    type RouteLike,
    type RouterLike,
} from '@/hooks/useGlobal.ts';

interface State {
    mounted: boolean;
}

interface LegacyGlobalContext {
    $route: RouteLike;
    $router: RouterLike;
    $t: (key: string) => string;
    $icons: Record<string, string>;
    $data: Record<string, any>;
    $options: {
        data?: (() => Record<string, any>) | undefined;
    };
}

const legacyMethodNames: Array<keyof GlobalMethods> = [
    '$reset',
    'average',
    'averageComma',
    'count',
    'underscore',
    'maskEmail',
    'maskPhone',
    'moment',
    'futurMoment',
    'filename',
    'getExtension',
    'decodeString',
    'navigateToLead',
    'getFullName',
    'getShortName',
    'timeHms',
    'emailIsValid',
    'validatePhone',
    'urlIsValid',
    'toCurrency',
    'toMoney',
    'formatPhone',
    'getLeadType',
    'getLeadTypeIcon',
    'formatExtension',
    'as_locale',
    'locale_dt',
    'localeDate',
    'locale_time',
    'empty',
    'updateQuery',
    'getFileName',
    'includeInLayout',
];

const legacyMethods = legacyMethodNames.reduce((methods, methodName) => {
    methods[methodName] = function legacyMethodBridge(
        this: LegacyGlobalContext,
        ...args: unknown[]
    ): unknown {
        const globalMethods = useGlobal({
            route: this.$route,
            router: this.$router,
            translate: (key: string) => this.$t(key),
            icons: this.$icons ?? {},
            resetData: () => {
                if (typeof this.$options.data !== 'function') {
                    return;
                }

                Object.assign(this.$data, this.$options.data.apply(this));
            },
        });

        return (globalMethods[methodName] as (...methodArgs: unknown[]) => unknown)(...args);
    };

    return methods;
}, {} as Record<keyof GlobalMethods, (this: LegacyGlobalContext, ...args: unknown[]) => unknown>);

const globalMixin = defineComponent({
    data(): State {
        return {
            mounted: false,
        };
    },

    methods: legacyMethods as Record<string, (...args: unknown[]) => unknown>,
});

export default globalMixin;
